import { useState, useEffect } from "react";
import API, { BACKEND_URL } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend, ReferenceLine } from "recharts";
import { TrendingUp, TrendingDown, AlertTriangle, ShieldAlert, Activity, BarChart3, Target, DollarSign, Clock, Users, Download, CheckCircle, Crosshair, ArrowRight, Layers } from "lucide-react";
import { useCurrency } from "@/lib/currency";

const CHART_COLORS = ["#0F172A", "#F97316", "#3B82F6", "#10B981", "#8B5CF6", "#EC4899", "#14B8A6", "#F59E0B"];

function IntelligenceTab({ data }) {
  const cur = useCurrency();
  const k = data.kpis;
  return (
    <div className="space-y-8">
      {/* KPI Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { icon: BarChart3, label: "Priced Projects", value: k.total_priced_projects, color: "text-slate-100", bg: "bg-slate-800/50" },
          { icon: DollarSign, label: "Pipeline Value", value: `${cur.symbol}${k.total_pipeline_fee.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`, color: "text-emerald-600", bg: "bg-emerald-500/10" },
          { icon: Target, label: "Avg Margin", value: `${k.avg_margin_pct}%`, color: "text-blue-600", bg: "bg-blue-500/10" },
          { icon: Clock, label: "Avg Hours/Project", value: k.avg_hours_per_project, color: "text-orange-600", bg: "bg-orange-50" },
          { icon: Users, label: "With Actuals", value: k.projects_with_actuals, color: "text-indigo-600", bg: "bg-indigo-50" },
          { icon: Activity, label: "Hours Variance", value: `${k.overall_hours_variance_pct > 0 ? "+" : ""}${k.overall_hours_variance_pct}%`, color: k.overall_hours_variance_pct > 0 ? "text-red-600" : "text-emerald-600", bg: k.overall_hours_variance_pct > 0 ? "bg-rose-500/10" : "bg-emerald-500/10" },
          { icon: AlertTriangle, label: "High Risk", value: k.high_risk_count, color: "text-amber-600", bg: "bg-amber-500/10" },
          { icon: ShieldAlert, label: "Low Confidence", value: k.low_confidence_count, color: "text-red-600", bg: "bg-rose-500/10" },
        ].map((kpi) => (
          <Card key={kpi.label} className="bg-slate-900 border border-slate-800 shadow-sm" data-testid={`intel-kpi-${kpi.label.toLowerCase().replace(/[\s\/]/g, "-")}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-8 h-8 rounded-lg ${kpi.bg} flex items-center justify-center`}><kpi.icon className={`w-4 h-4 ${kpi.color}`} /></div>
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{kpi.label}</span>
              </div>
              <p className={`font-mono text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {data.avg_hours_by_stage.length > 0 && (
          <Card className="bg-slate-900 border border-slate-800 shadow-sm">
            <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Clock className="w-4 h-4 text-blue-500" />Average Hours by Stage</CardTitle></CardHeader>
            <CardContent className="p-4">
              <div className="h-64" data-testid="chart-hours-by-stage">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.avg_hours_by_stage} margin={{ top: 10, right: 10, left: 0, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="stage" tick={{ fontSize: 10, fill: "#94A3B8" }} angle={-20} textAnchor="end" height={50} />
                    <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, background: "#1E293B", border: "1px solid #334155", color: "#E2E8F0" }} formatter={(v) => [`${v} hrs`, "Avg Hours"]} />
                    <Bar dataKey="avg_hours" radius={[4, 4, 0, 0]}>
                      {data.avg_hours_by_stage.map((entry, i) => <Cell key={entry.stage || `stage-${i}`} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {data.avg_fee_by_sector.length > 0 && (
          <Card className="bg-slate-900 border border-slate-800 shadow-sm">
            <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4"><CardTitle className="text-sm font-semibold flex items-center gap-2"><DollarSign className="w-4 h-4 text-emerald-500" />Average Fee by Sector</CardTitle></CardHeader>
            <CardContent className="p-4">
              <div className="h-64" data-testid="chart-fee-by-sector">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.avg_fee_by_sector} margin={{ top: 10, right: 10, left: 10, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="sector" tick={{ fontSize: 10, fill: "#94A3B8" }} />
                    <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} tickFormatter={(v) => `${cur.symbol}${(v/1000).toFixed(0)}k`} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, background: "#1E293B", border: "1px solid #334155", color: "#E2E8F0" }} formatter={(v) => [`${cur.symbol}${v.toLocaleString()}`, "Avg Fee"]} />
                    <Bar dataKey="avg_fee" radius={[4, 4, 0, 0]}>
                      {data.avg_fee_by_sector.map((entry, i) => <Cell key={entry.sector || `sector-${i}`} fill={CHART_COLORS[(i + 1) % CHART_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {data.avg_effort_by_discipline.length > 0 && (
          <Card className="bg-slate-900 border border-slate-800 shadow-sm">
            <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Users className="w-4 h-4 text-indigo-500" />Average Effort by Discipline</CardTitle></CardHeader>
            <CardContent className="p-4">
              <div className="h-64" data-testid="chart-effort-by-discipline">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.avg_effort_by_discipline} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="discipline" tick={{ fontSize: 10, fill: "#94A3B8" }} />
                    <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, background: "#1E293B", border: "1px solid #334155", color: "#E2E8F0" }} formatter={(v) => [`${v} hrs`, "Avg Hours"]} />
                    <Bar dataKey="avg_hours" radius={[4, 4, 0, 0]}>
                      {data.avg_effort_by_discipline.map((entry, i) => <Cell key={entry.discipline || `disc-${i}`} fill={CHART_COLORS[(i + 2) % CHART_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Estimate vs Actual Variance */}
        <Card className="bg-slate-900 border border-slate-800 shadow-sm">
          <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Activity className="w-4 h-4 text-blue-500" />Estimate vs Actual Variance</CardTitle></CardHeader>
          <CardContent className="p-0">
            {data.variance_data.length > 0 ? (
              <Table>
                <TableHeader><TableRow className="bg-slate-800/50">
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4">Project</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4 text-right">Est.</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4 text-right">Actual</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4 text-right">Var %</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4">Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.variance_data.map((v) => (
                    <TableRow key={v.project_id}>
                      <TableCell className="px-4 py-2.5 text-sm font-medium">{v.name}</TableCell>
                      <TableCell className="px-4 py-2.5 font-mono text-sm text-right">{v.estimated_hours}h</TableCell>
                      <TableCell className="px-4 py-2.5 font-mono text-sm text-right">{v.actual_hours}h</TableCell>
                      <TableCell className="px-4 py-2.5 text-right">
                        <span className={`inline-flex items-center gap-0.5 font-mono text-sm font-bold ${v.variance_pct > 0 ? "text-red-600" : "text-emerald-600"}`}>
                          {v.variance_pct > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          {v.variance_pct > 0 ? "+" : ""}{v.variance_pct}%
                        </span>
                      </TableCell>
                      <TableCell className="px-4 py-2.5">{v.completion_status ? <Badge variant="outline" className="text-xs">{v.completion_status}</Badge> : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-sm text-slate-400">Record actuals on projects to see variance analysis.</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row: High Risk + Low Confidence */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-slate-900 border border-amber-500/30 shadow-sm">
          <CardHeader className="border-b border-amber-500/30 bg-amber-500/5 py-3 px-4"><CardTitle className="text-sm font-semibold flex items-center gap-2 text-amber-400"><AlertTriangle className="w-4 h-4" />High Risk Projects ({data.high_risk_projects.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            {data.high_risk_projects.length > 0 ? (
              <Table>
                <TableHeader><TableRow className="bg-amber-500/5">
                  <TableHead className="text-xs uppercase font-semibold text-amber-500 px-4">Project</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-amber-500 px-4 text-right">Risk Hrs</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-amber-500 px-4 text-right">High Prob</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-amber-500 px-4">Conf.</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-amber-500 px-4 text-right">Fee</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.high_risk_projects.map((p) => (
                    <TableRow key={p.project_id}>
                      <TableCell className="px-4 py-2.5"><span className="text-sm font-medium">{p.name}</span><span className="text-xs text-slate-500 ml-1">({p.sector})</span></TableCell>
                      <TableCell className="px-4 py-2.5 font-mono text-sm text-right font-bold text-amber-500">{p.total_risk_hours}h</TableCell>
                      <TableCell className="px-4 py-2.5 font-mono text-sm text-right">{p.high_probability_risks}</TableCell>
                      <TableCell className="px-4 py-2.5"><Badge variant="outline" className={`text-xs ${p.confidence === "Low" ? "border-red-500/40 text-red-400" : p.confidence === "High" ? "border-emerald-500/40 text-emerald-400" : "border-amber-500/40 text-amber-400"}`}>{p.confidence}</Badge></TableCell>
                      <TableCell className="px-4 py-2.5 font-mono text-sm text-right">{cur.symbol}{p.total_fee.toLocaleString("en-GB", { maximumFractionDigits: 0 })}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-sm text-slate-400">No high risk projects identified.</div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border border-red-500/30 shadow-sm">
          <CardHeader className="border-b border-red-500/30 bg-rose-500/5 py-3 px-4"><CardTitle className="text-sm font-semibold flex items-center gap-2 text-red-400"><ShieldAlert className="w-4 h-4" />Low Confidence Pricing ({data.low_confidence_records.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            {data.low_confidence_records.length > 0 ? (
              <Table>
                <TableHeader><TableRow className="bg-rose-500/5">
                  <TableHead className="text-xs uppercase font-semibold text-red-500 px-4">Project</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-red-500 px-4">Confidence</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-red-500 px-4 text-right">Score</TableHead>
                  <TableHead className="text-xs uppercase font-semibold text-red-500 px-4 text-right">Fee at Risk</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.low_confidence_records.map((p) => (
                    <TableRow key={p.project_id}>
                      <TableCell className="px-4 py-2.5"><span className="text-sm font-medium">{p.name}</span><span className="text-xs text-slate-500 ml-1">({p.sector})</span></TableCell>
                      <TableCell className="px-4 py-2.5"><Badge variant="outline" className={`text-xs ${p.confidence === "Low" ? "border-red-500/40 text-red-400" : p.confidence === "Unassessed" ? "border-slate-700 text-slate-500" : "border-amber-500/40 text-amber-400"}`}>{p.confidence}</Badge></TableCell>
                      <TableCell className="px-4 py-2.5 font-mono text-sm text-right">{p.avg_score}/5</TableCell>
                      <TableCell className="px-4 py-2.5 font-mono text-sm text-right font-bold text-red-400">{cur.symbol}{p.total_fee.toLocaleString("en-GB", { maximumFractionDigits: 0 })}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-sm text-slate-400">All projects have adequate confidence levels.</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function AccuracyTab({ data }) {
  const s = data.summary;

  const accuracyColor = (ratio) => {
    if (ratio >= 0.95 && ratio <= 1.05) return "text-emerald-400";
    if (ratio >= 0.85 && ratio <= 1.15) return "text-amber-400";
    return "text-red-400";
  };
  const varianceColor = (pct) => {
    const abs = Math.abs(pct);
    if (abs <= 5) return "text-emerald-400";
    if (abs <= 15) return "text-amber-400";
    return "text-red-400";
  };

  const sectorChartData = data.by_sector.map(d => ({
    ...d,
    variance: d.variance_pct,
  }));
  const stageChartData = data.by_stage.map(d => ({
    ...d,
    variance: d.variance_pct,
  }));
  const disciplineChartData = data.by_discipline.map(d => ({
    ...d,
    variance: d.variance_pct,
  }));

  return (
    <div className="space-y-8" data-testid="pricing-accuracy-tab">
      {/* Summary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          { icon: Crosshair, label: "Projects Tracked", value: s.projects_tracked, color: "text-slate-100", bg: "bg-slate-800/50" },
          { icon: CheckCircle, label: "Finalized", value: s.projects_finalized, color: "text-emerald-400", bg: "bg-emerald-500/10" },
          { icon: Clock, label: "Est. Hours", value: s.total_estimated_hours.toLocaleString(), color: "text-blue-400", bg: "bg-blue-500/10" },
          { icon: Activity, label: "Actual Hours", value: s.total_actual_hours.toLocaleString(), color: "text-orange-400", bg: "bg-orange-500/10" },
          { icon: Target, label: "Overall Accuracy", value: `${(s.overall_accuracy * 100).toFixed(1)}%`, color: accuracyColor(s.overall_accuracy), bg: "bg-slate-800/50" },
          { icon: TrendingUp, label: "Variance", value: `${s.overall_variance_pct > 0 ? "+" : ""}${s.overall_variance_pct}%`, color: varianceColor(s.overall_variance_pct), bg: s.overall_variance_pct > 0 ? "bg-rose-500/10" : "bg-emerald-500/10" },
        ].map((kpi) => (
          <Card key={kpi.label} className="bg-slate-900 border border-slate-800 shadow-sm" data-testid={`accuracy-kpi-${kpi.label.toLowerCase().replace(/[\s\/]/g, "-")}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-7 h-7 rounded-lg ${kpi.bg} flex items-center justify-center`}><kpi.icon className={`w-3.5 h-3.5 ${kpi.color}`} /></div>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{kpi.label}</span>
              </div>
              <p className={`font-mono text-xl font-bold ${kpi.color}`}>{kpi.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {s.projects_tracked === 0 && (
        <Card className="bg-slate-900 border border-slate-800">
          <CardContent className="p-12 text-center">
            <Crosshair className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-slate-300 mb-1">No Accuracy Data Yet</h3>
            <p className="text-sm text-slate-500 max-w-md mx-auto">Log actual hours via the Timesheets tab on your projects. Once projects have both estimated and actual hours, accuracy analysis will appear here.</p>
          </CardContent>
        </Card>
      )}

      {s.projects_tracked > 0 && (
        <>
          {/* Accuracy Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Sector Accuracy */}
            <Card className="bg-slate-900 border border-slate-800 shadow-sm">
              <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2"><Layers className="w-4 h-4 text-blue-500" />Accuracy by Sector</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {sectorChartData.length > 0 ? (
                  <div className="h-64" data-testid="chart-accuracy-by-sector">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={sectorChartData} margin={{ top: 10, right: 10, left: -10, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="sector" tick={{ fontSize: 10, fill: "#94A3B8" }} />
                        <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, background: "#1E293B", border: "1px solid #334155", color: "#E2E8F0" }} />
                        <Legend wrapperStyle={{ fontSize: 10 }} />
                        <Bar dataKey="estimated" name="Estimated" fill="#3B82F6" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="actual" name="Actual" fill="#F97316" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="text-center py-8 text-sm text-slate-500">No sector data available.</div>
                )}
              </CardContent>
            </Card>

            {/* Stage Accuracy */}
            <Card className="bg-slate-900 border border-slate-800 shadow-sm">
              <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2"><BarChart3 className="w-4 h-4 text-emerald-500" />Accuracy by Stage</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {stageChartData.length > 0 ? (
                  <div className="h-64" data-testid="chart-accuracy-by-stage">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stageChartData} margin={{ top: 10, right: 10, left: -10, bottom: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="stage" tick={{ fontSize: 9, fill: "#94A3B8" }} angle={-20} textAnchor="end" height={50} />
                        <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, background: "#1E293B", border: "1px solid #334155", color: "#E2E8F0" }} />
                        <Legend wrapperStyle={{ fontSize: 10 }} />
                        <Bar dataKey="estimated" name="Estimated" fill="#3B82F6" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="actual" name="Actual" fill="#F97316" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="text-center py-8 text-sm text-slate-500">No stage data available.</div>
                )}
              </CardContent>
            </Card>

            {/* Discipline Accuracy */}
            <Card className="bg-slate-900 border border-slate-800 shadow-sm">
              <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2"><Users className="w-4 h-4 text-violet-500" />Accuracy by Discipline</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {disciplineChartData.length > 0 ? (
                  <div className="h-64" data-testid="chart-accuracy-by-discipline">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={disciplineChartData} margin={{ top: 10, right: 10, left: -10, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="discipline" tick={{ fontSize: 10, fill: "#94A3B8" }} />
                        <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, background: "#1E293B", border: "1px solid #334155", color: "#E2E8F0" }} />
                        <Legend wrapperStyle={{ fontSize: 10 }} />
                        <Bar dataKey="estimated" name="Estimated" fill="#3B82F6" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="actual" name="Actual" fill="#F97316" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="text-center py-8 text-sm text-slate-500">No discipline data available.</div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Variance by Sector Table */}
          {data.by_sector.length > 0 && (
            <Card className="bg-slate-900 border border-slate-800 shadow-sm">
              <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2"><Target className="w-4 h-4 text-blue-500" />Sector Accuracy Detail</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow className="bg-slate-800/50">
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4">Sector</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4 text-right">Estimated</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4 text-right">Actual</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4 text-right">Accuracy</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4 text-right">Variance</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4 text-right">Projects</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.by_sector.map((row) => (
                      <TableRow key={row.sector} data-testid={`sector-row-${row.sector}`}>
                        <TableCell className="px-4 py-2.5 text-sm font-medium">{row.sector}</TableCell>
                        <TableCell className="px-4 py-2.5 font-mono text-sm text-right">{row.estimated}h</TableCell>
                        <TableCell className="px-4 py-2.5 font-mono text-sm text-right">{row.actual}h</TableCell>
                        <TableCell className="px-4 py-2.5 text-right">
                          <span className={`font-mono text-sm font-bold ${accuracyColor(row.accuracy)}`}>{(row.accuracy * 100).toFixed(1)}%</span>
                        </TableCell>
                        <TableCell className="px-4 py-2.5 text-right">
                          <span className={`inline-flex items-center gap-0.5 font-mono text-sm font-bold ${varianceColor(row.variance_pct)}`}>
                            {row.variance_pct > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                            {row.variance_pct > 0 ? "+" : ""}{row.variance_pct}%
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-2.5 font-mono text-sm text-right">{row.project_count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Project-Level Accuracy Table */}
          <Card className="bg-slate-900 border border-slate-800 shadow-sm">
            <CardHeader className="border-b border-slate-800 bg-slate-800/50 py-3 px-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2"><Activity className="w-4 h-4 text-orange-500" />Project-Level Accuracy ({data.projects.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {data.projects.length > 0 ? (
                <Table>
                  <TableHeader><TableRow className="bg-slate-800/50">
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4">Project</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4">Sector</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4 text-right">Est. Hours</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4 text-right">Actual</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4 text-right">Variance</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4 text-right">Accuracy</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-slate-500 px-4">Status</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.projects.map((p) => (
                      <TableRow key={p.project_id} data-testid={`accuracy-project-row-${p.project_id}`}>
                        <TableCell className="px-4 py-2.5 text-sm font-medium">{p.name}</TableCell>
                        <TableCell className="px-4 py-2.5 text-xs text-slate-400">{p.sector || "—"}</TableCell>
                        <TableCell className="px-4 py-2.5 font-mono text-sm text-right">{p.estimated_hours}h</TableCell>
                        <TableCell className="px-4 py-2.5 font-mono text-sm text-right">{p.actual_hours}h</TableCell>
                        <TableCell className="px-4 py-2.5 text-right">
                          <span className={`inline-flex items-center gap-0.5 font-mono text-sm font-bold ${varianceColor(p.variance_pct)}`}>
                            {p.variance_pct > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                            {p.variance_pct > 0 ? "+" : ""}{p.variance_pct}%
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-2.5 text-right">
                          <span className={`font-mono text-sm font-bold ${accuracyColor(p.accuracy_ratio)}`}>{(p.accuracy_ratio * 100).toFixed(1)}%</span>
                        </TableCell>
                        <TableCell className="px-4 py-2.5">
                          <div className="flex items-center gap-1">
                            {p.finalized && <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />}
                            <Badge variant="outline" className={`text-[10px] ${p.finalized ? "border-emerald-500/40 text-emerald-400" : "border-slate-700 text-slate-500"}`}>{p.status}</Badge>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-sm text-slate-400">No project accuracy records yet.</div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

export default function PricingIntelligence() {
  const [activeTab, setActiveTab] = useState("intelligence");
  const [intelData, setIntelData] = useState(null);
  const [accuracyData, setAccuracyData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      API.get("/analytics/intelligence").then(r => r.data).catch(() => null),
      API.get("/analytics/pricing-accuracy").then(r => r.data).catch(() => null),
    ]).then(([intel, accuracy]) => {
      setIntelData(intel);
      setAccuracyData(accuracy);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="space-y-6">
      <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-100">Pricing Intelligence</h1>
      <div className="grid grid-cols-4 gap-4">{Array.from({ length: 8 }).map((_, i) => <Card key={`skeleton-${i}`} className="bg-slate-900 border border-slate-800"><CardContent className="p-5"><div className="h-12 bg-slate-800 rounded animate-pulse" /></CardContent></Card>)}</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="intelligence-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-100">Pricing Intelligence</h1>
          <p className="text-sm text-muted-foreground mt-1">Aggregated insights and pricing accuracy analysis</p>
        </div>
        <div className="flex gap-2">
          {activeTab === "intelligence" && (
            <>
              <a href={`${BACKEND_URL}/api/analytics/export/intelligence`}><Button variant="outline" size="sm" className="border-slate-800 h-8" data-testid="export-intelligence-csv"><Download className="w-3 h-3 mr-1" />CSV</Button></a>
              <a href={`${BACKEND_URL}/api/analytics/export/benchmarking-excel`}><Button variant="outline" size="sm" className="border-slate-800 h-8" data-testid="export-intelligence-excel"><Download className="w-3 h-3 mr-1" />Excel</Button></a>
              <a href={`${BACKEND_URL}/api/analytics/export/stages`}><Button variant="outline" size="sm" className="border-slate-800 h-8" data-testid="export-stages-csv"><Download className="w-3 h-3 mr-1" />Stage Data</Button></a>
            </>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-slate-900 border border-slate-800 rounded-lg p-1 w-fit" data-testid="intelligence-tabs">
        <button
          onClick={() => setActiveTab("intelligence")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === "intelligence" ? "bg-slate-800 text-white shadow-sm" : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50"}`}
          data-testid="tab-intelligence"
        >
          <BarChart3 className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
          Intelligence
        </button>
        <button
          onClick={() => setActiveTab("accuracy")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === "accuracy" ? "bg-slate-800 text-white shadow-sm" : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50"}`}
          data-testid="tab-pricing-accuracy"
        >
          <Crosshair className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
          Pricing Accuracy
          {accuracyData?.summary?.projects_tracked > 0 && (
            <Badge variant="outline" className="ml-2 text-[10px] px-1.5 py-0 border-blue-500/40 text-blue-400">{accuracyData.summary.projects_tracked}</Badge>
          )}
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === "intelligence" && intelData ? (
        <IntelligenceTab data={intelData} />
      ) : activeTab === "intelligence" ? (
        <div className="text-center py-20 text-sm text-slate-400">Failed to load intelligence data.</div>
      ) : null}

      {activeTab === "accuracy" && accuracyData ? (
        <AccuracyTab data={accuracyData} />
      ) : activeTab === "accuracy" ? (
        <div className="text-center py-20 text-sm text-slate-400">Failed to load pricing accuracy data.</div>
      ) : null}
    </div>
  );
}
